/* @flow */

import React from 'react';
import styled from '@emotion/styled';
import pluralize from 'pluralize';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';

import Modal from 'app/components/commons/Modal';
import Button from 'app/components/commons/Button';
import t from 'app/utils/translate';

type Props = {
  onCancel: () => void,
  successes?: number,
  failures?: Array<string>
};

const defaultProps = {
  failures: [],
  successes: 0
};

const Wrapper = styled.div(
  {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  ({ theme }) => ({
    fontSize: theme.fontSize.small,
    paddingTop: theme.spacing.default
  })
);

const Body = styled.div(({ theme, paddingTop = true, paddingBottom = true }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  width: '100%',
  paddingTop: paddingTop ? theme.spacing.default : 'auto',
  paddingBottom: paddingBottom ? theme.spacing.default : 'auto'
}));

const Bold = styled.span(({ theme }) => ({
  fontWeight: theme.fontWeight.demiBold,
  fontSize: theme.fontSize.medium
}));

const ButtonContainer = styled.div(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'center',
  '& > * ': {
    marginRight: theme.spacing.small
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  width: '150px',
  margin: theme.spacing.xsmall,
  marginBottom: theme.spacing.large,
  textAlign: 'center',
  [theme.mediaQuery.mobileSmallOnly]: {
    width: '120px'
  }
}));

const SuccessIcon = styled(FontAwesomeIcon)(({ theme }) => ({
  color: theme.color.success
}));

const CrunchSuccessModal = ({ successes, failures, onCancel }: Props) => (
  <Modal isOpen onCancel={onCancel} shouldCloseOnOverlayClick size="medium">
    <Wrapper>
      {successes > 0 && <SuccessIcon icon={faCheckCircle} size="4x" />}
      <Body>
        {successes > 0 && (
          <Bold>
            {successes} {pluralize('team', successes)} {pluralize('has', successes)} been
            successfully submitted!
          </Bold>
        )}
        {failures.length > 0 && (
          <Bold>
            {failures.length} {pluralize('team', failures.length)} already{' '}
            {failures.length > 1 ? 'exist' : 'exists'}.
          </Bold>
        )}
      </Body>
      <ButtonContainer>
        <StyledButton size="medium" kind="secondary" link="/lobby">
          {t('Lobby')}
        </StyledButton>
        <StyledButton size="medium" kind="primary" link="/my-teams/upcoming-teams">
          {t('My Teams')}
        </StyledButton>
      </ButtonContainer>
    </Wrapper>
  </Modal>
);

CrunchSuccessModal.defaultProps = defaultProps;

export default CrunchSuccessModal;
